<template>
  <div id="app">
    <UrlParams v-model="urlParams" />
    <div class="container">
      <div class="m-block m-block--text">
        <div
          class="m-block__content f-rte f-rte--block m-block__content-topic-description"
        >
          <p
            class="topic-description"
            v-if="notesData[topic]?.general"
            v-html="notesData[topic].general"
          ></p>
        </div>
        <div class="m-block__aside gender-portal-header">
          <SVGIcon :name="topic" class="topic-logo" />
          <DataGetter
            :childElementType="null"
            :url="`${apiRoute}/list/Topic`"
            :formatter="
              (data) =>
                data.sort((a, b) => {
                  if (
                    a.toLowerCase().includes('senior') &&
                    !b.toLowerCase().includes('employment')
                  ) {
                    return -1;
                  } else if (
                    !a.toLowerCase().includes('employment') &&
                    b.toLowerCase().includes('senior')
                  ) {
                    return 1;
                  } else return a - b;
                })
            "
            v-slot="{ data }"
          >
            <Dropdown
              label="Topic"
              v-model="topic"
              :options="data || []"
              :resetOnInvalid="true"
              @input="topicChanged()"
            />
          </DataGetter>
        </div>
      </div>
      <div class="m-block m-block--links">
        <div class="m-block__aside">
          <ul class="m-block__links">
            <li>
              <a
                class="a-link a-link-icon a-link-icon--secondary"
                :href="fullDownloadLink"
              >
                <span>
                  Download the full {{ topic }} Gender and Energy dataset
                </span>
                <span class="icon icon--circle-arrow">
                  <svg>
                    <title>circle-arrow</title>
                    <use xlink:href="#icon--circle-arrow"></use>
                  </svg>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="container">
      <ControlGroup v-if="indicatorParameter">
        <DataExplorerParam
          class="control-group--item"
          :name="indicatorParameter.name"
          :type="indicatorParameter.type"
          v-model="parameterValues[indicatorParameter.id]"
          :url="indicatorParameter.url"
          :urlParams="
            indicatorParameter.dynamicParams
              ? {
                  ...getParams(indicatorParameter.dynamicParams),
                  ...(indicatorParameter.staticParams || {}),
                }
              : { ...(indicatorParameter.staticParams || {}) }
          "
          :options="indicatorParameter.options"
          :config="indicatorParameter.config || {}"
        />
      </ControlGroup>

      <ControlGroup>
        <DataExplorerParam
          v-for="(parameter, index) of parameters"
          :key="`param-${index}`"
          class="control-group--item"
          :name="parameter.name"
          :type="parameter.type"
          v-model="parameterValues[parameter.id]"
          :url="parameter.url"
          :urlParams="
            parameter.dynamicParams
              ? {
                  ...getParams(parameter.dynamicParams),
                  ...(parameter.staticParams || {}),
                }
              : { ...(parameter.staticParams || {}) }
          "
          :options="parameter.options"
          :config="parameter.config || {}"
        />
      </ControlGroup>
    </div>

    <Tabs class="container">
      <Tab key="overview" title="Overview">
        <DataExplorer
          :indicators="indicators"
          :defaultParameterValues="parameterValues"
        />
      </Tab>

      <Tab key="metadata" title="Metadata">
        <MetaDataView :topic="topic" />
      </Tab>

      <Tab
        v-if="parameterValues['Indicator']?.includes('Gender wage gap')"
        key="comparison"
        title="Indicator comparison"
      >
        <IndicatorComparison
          :parameterValues="parameterValues"
          :apiRoute="apiRoute"
        />
      </Tab>
    </Tabs>
  </div>
</template>

<script>
import SVGIcon from "./SVGIcon.vue";
import IndicatorComparison from "./IndicatorComparison.vue";
import MetaDataView from "./MetaDataView.vue";

import {
  Dropdown,
  DataGetter,
 UrlParams,
  ControlGroup,
  Tab,
  Tabs,
} from "@international-energy-agency/vue-ui";
// } from "../../vue-ui/src/main.js";
import {
  DataExplorer,
  DataExplorerParam,
} from "@international-energy-agency/vue-ui/highcharts";
// } from "../../vue-ui/src/highcharts.js";

import { notesData, categoryIndicators } from "./data/data";

export default {
  name: "App",
  components: {
    SVGIcon,
    IndicatorComparison,
    MetaDataView,
    ControlGroup,
    DataExplorer,
    DataExplorerParam,
    Dropdown,
    DataGetter,
   UrlParams,
    Tab,
    Tabs,
  },
  props: {
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notesData,
      categoryIndicators,
      topic: "Employment",
      parameterValues: {
        Indicator: null,
        "Technology or Sector": [],
        Country: [],
        "Number of observations": 5,
        Year: null,
        "Indicator Categories": null,
        viewType1: "Indicator Categories",
        viewType2: "Technology or Sector",
      },
    };
  },
  mounted() {
    this.setUpDefaultValues(this.topic, this.parameterValues.Indicator);
  },
  computed: {
    apiRoute() {
      const { test } = this;
      return `https://api.${test ? "test.iea" : "iea"}.org/gender-diversity`;
    },
    urlParams: {
      get: function () {
        return {
          Topic: this.topic,
          Indicator: this.parameterValues.Indicator,
          Year: this.parameterValues.Year
        };
      },
      set: function (newValue) {
        this.topic = newValue.Topic;
        this.parameterValues.Indicator = newValue.Indicator;
        this.parameterValues.Year = newValue.Year;
      },
    },
    indicatorParameter() {
      const { apiRoute } = this;
      return {
        id: "Indicator",
        name: "Indicator",
        url: `${apiRoute}/list/Indicator`,
        staticParams: {
          Topic: this.topic,
        },
        type: "select",
      };
    },
    parameters() {
      const { apiRoute } = this;
      return [
        {
          id: "Country",
          name: "Countries",
          url: `${apiRoute}/list/Country`,
          staticParams: {
            Topic: this.topic,
            Indicator: this.parameterValues["Indicator"],
          },
          type: "multiselect",
          config: {
            maxItems: 6,
          },
        },
        {
          id: "Technology or Sector",
          name: this.topic === "Innovation" ? "Technologies" : "Sectors",
          url: `${apiRoute}/list/Technology or Sector`,
          staticParams: {
            Topic: this.topic,
            Indicator: this.parameterValues["Indicator"],
          },
          type: "multiselect",
          config: {
            maxItems: 3,
          },
        },
        {
          id: "Number of observations",
          name: "Min. number of observations in sample",
          type: "select",
          options: [5, 10, 20, 50, 100, 1000, 10000],
        },
      ];
    },
    indicators() {
      const { apiRoute } = this;
      return [
        {
          url: apiRoute,
          isVisible: () =>
            this.categoryIndicators.includes(this.parameterValues.Indicator),
          type: "small-multiples",
          notes: this.indicatorNotes,
          extraParams: [
            {
              id: "Year",
              name: "Year",
              url: `${apiRoute}/list/Year`,
              staticParams: {
                Topic: this.topic,
                Indicator: this.parameterValues["Indicator"],
              },
              formatter: (data) => [...data].sort((a, b) => b - a),
              type: "select",
            },
            {
              id: "viewType1",
              name: "View",
              type: "buttonGroup",
              options: [
                { label: "Category", value: "Indicator Categories" },
                { label: "Sector", value: "Technology or Sector" },
              ],
            },
          ],
          params: ["Year"],
          staticParams: {
            Indicator: this.parameterValues["Indicator"],
            Country: this.parameterValues["Country"],
            "Technology or Sector": this.parameterValues[
              "Technology or Sector"
            ],
            "Number of observations": this.parameterValues[
              "Number of observations"
            ],
            Topic: this.topic,
          },
          config: {
            title: (paramValues) => {
              if (this.parameterValues["Indicator"]) {
                return `${
                  this.parameterValues["Indicator"]
                } for the selected sectors${
                  this.parameterValues["Technology or Sector"].length
                    ? ` (${this.parameterValues["Technology or Sector"].join(
                        ", "
                      )})`
                    : ""
                } and categories, ${paramValues["Year"] || "latest year"}`;
              } else {
                return null;
              }
            },
            unit: (_, data) => {
              return this.getUnitValue(data[0]?.Unit);
            },
            chartType: "column",
            xAxisDimension: "Country",
            yAxisDimension: (paramValues) =>
              paramValues.viewType1 === "Indicator Categories"
                ? "Technology or Sector"
                : "Indicator Categories",
            groupDimension: (paramValues) => paramValues.viewType1,
            valueDimension: "Value",
            dataFormatter: (data) =>
              data.sort((a, b) => {
                if (
                  parseInt(a["Indicator Categories Order"]) <
                  parseInt(b["Indicator Categories Order"])
                )
                  return -1;
                else if (
                  parseInt(a["Indicator Categories Order"]) >
                  parseInt(b["Indicator Categories Order"])
                )
                  return 1;
                else return 0;
              }),
            chartOptions: {
              chart: {
                height: 250,
              },
              yAxis: {
                reversedStacks: true,
                visible: true,
              },
              plotOptions: {
                column: {
                  stacking: undefined,
                },
              },
            },
            downloadLink: () => this.downloadLink,
          },
        },
        {
          url: apiRoute,
          isVisible: () =>
            !this.categoryIndicators.includes(this.parameterValues.Indicator),
          type: "chart",
          notes: this.indicatorNotes,
          extraParams: [
            {
              id: "Year",
              name: "Year",
              url: `${apiRoute}/list/Year`,
              staticParams: {
                Topic: this.topic,
                Indicator: this.parameterValues["Indicator"],
              },
              type: "select",
              formatter: (data) => [...data].sort((a, b) => b - a),
            },
          ],
          params: ["Year"],
          staticParams: {
            Indicator: this.parameterValues["Indicator"],
            Country: this.parameterValues["Country"],
            "Number of observations": this.parameterValues[
              "Number of observations"
            ],
            "Technology or Sector": this.parameterValues[
              "Technology or Sector"
            ],
            Topic: this.topic,
          },
          config: {
            title: (paramValues) => {
              if (this.parameterValues["Indicator"]) {
                return `${
                  this.parameterValues["Indicator"]
                } for the selected sectors${
                  this.parameterValues["Technology or Sector"].length
                    ? ` (${this.parameterValues["Technology or Sector"].join(
                        ", "
                      )})`
                    : ""
                }, ${paramValues["Year"]}`;
              } else {
                return null;
              }
            },
            unit: (_, data) => {
              return this.getUnitValue(data[0]?.Unit);
            },
            chartType: "column",
            xAxisDimension: "Country",
            yAxisDimension: "Technology or Sector",
            valueDimension: "Value",
            chartOptions: {
              yAxis: {
                reversedStacks: true,
                endOnTick: true,
              },
              tooltip: {
                valueDecimals: 1,
              },
              plotOptions: {
                column: {
                  stacking: undefined,
                },
              },
            },
            customSlicer: (
              data,
              xAxisDimension,
              yAxisDimension,
              valueDimension
            ) => {
              if (!data || data.length === 0) {
                return { slicedData: [], xAxisCategories: null };
              }

              const categories = data.reduce(
                (categories, dataRow) => {
                  if (!categories.x.includes(dataRow[xAxisDimension]))
                    categories.x.push(dataRow[xAxisDimension]);
                  if (!categories.y.includes(dataRow[yAxisDimension]))
                    categories.y.push(dataRow[yAxisDimension]);
                  return categories;
                },
                { x: [], y: [] }
              );
              categories.x = categories.x.sort();

              const slicedData = categories.y.map((seriesName) => {
                const seriesData = {
                  year: data[0].Year,
                  unit: data[0].Unit,
                  name: seriesName,
                  data: categories.x.map((xCategory) => {
                    const rowData = data.filter(
                      (dataRow) =>
                        dataRow[xAxisDimension] === xCategory &&
                        dataRow[yAxisDimension] === seriesName
                    );
                    let rawValue = rowData ? rowData[valueDimension] : null;
                    if (rowData.length > 1) {
                      rawValue = rowData.reduce((total, row) => {
                        return total + row[valueDimension];
                      }, 0);
                      rawValue = rawValue / rowData.length;
                    } else if (rowData.length === 1) {
                      rawValue = rowData[0][valueDimension];
                    }
                    const finalValue = Number.isNaN(parseFloat(rawValue))
                      ? rawValue
                      : parseFloat(rawValue);
                    return [xCategory, finalValue];
                  }),
                };
                return seriesData;
              });

              return {
                slicedData,
                xAxisCategories: categories.x.map((x) => x.toString()),
              };
            },
            downloadLink: () => this.downloadLink,
          },
        },
        {
          url: apiRoute,
          params: ["Indicator Categories"],
          type: "small-multiples",
          notes: this.indicatorNotes,
          staticParams: {
            Indicator: this.parameterValues["Indicator"],
            Country: this.parameterValues["Country"],
            "Technology or Sector": this.parameterValues[
              "Technology or Sector"
            ],
            "Number of observations": this.parameterValues[
              "Number of observations"
            ],
            Topic: this.topic,
          },
          extraParams: [
            {
              id: "Indicator Categories",
              name: "Category",
              url: `${apiRoute}/list/Indicator%20Categories`,
              staticParams: { Indicator: this.parameterValues["Indicator"] },
              type: "select",
              // formatter: (data) => {
              //   return [...data]
              //     .filter((elt) => !!elt)
              //     .sort((a, b) => {
              //       if (a.label < b.label) return -1;
              //       else if (a.label > b.label) return 1;
              //       else return 0;
              //     });
              // },
              isVisible: () =>
                this.categoryIndicators.includes(
                  this.parameterValues.Indicator
                ),
            },
            {
              id: "viewType2",
              name: "View",
              type: "buttonGroup",
              options: [
                { label: "Country", value: "Country" },
                { label: "Sector", value: "Technology or Sector" },
              ],
            },
          ],
          config: {
            title: (paramValues) => {
              if (this.parameterValues["Indicator"]) {
                return `Evolution over time of the ${this.parameterValues[
                  "Indicator"
                ].toLowerCase()}${
                  this.categoryIndicators.includes(
                    this.parameterValues["Indicator"]
                  )
                    ? `: ${
                        paramValues["Indicator Categories"]?.toLowerCase() || ""
                      }`
                    : ""
                } for the selected sectors${
                  this.parameterValues["Technology or Sector"].length
                    ? ` (${this.parameterValues["Technology or Sector"].join(
                        ", "
                      )})`
                    : ""
                }`;
              } else {
                return null;
              }
            },
            unit: (_, data) => {
              return this.getUnitValue(data[0]?.Unit);
            },
            chartType: "line",
            xAxisDimension: "Year",
            yAxisDimension: (paramValues) =>
              paramValues.viewType2 === "Technology or Sector"
                ? "Country"
                : "Technology or Sector",
            groupDimension: (paramValues) => paramValues.viewType2,
            valueDimension: "Value",
            chartOptions: {
              chart: {
                height: 250,
              },
              yAxis: {
                visible: true,
              },
            },
            downloadLink: () => this.downloadLink,
          },
        },
      ];
    },
    indicatorNotes() {
      if (!this.parameterValues["Indicator"]) {
        return null;
      }

      const notesArray = [];
      if (notesData[this.topic]["source"]) {
        notesArray.push(notesData[this.topic]["source"]);
      }

      if (
        notesData[this.topic]["indicators"] &&
        Object.keys(notesData[this.topic]["indicators"]).length
      ) {
        const noteKey = Object.keys(
          notesData[this.topic]["indicators"]
        ).find((key) =>
          this.parameterValues["Indicator"]
            .toLowerCase()
            .includes(key.toLowerCase())
        );
        notesArray.push(notesData[this.topic]["indicators"][noteKey]);
      }

      return notesArray.join("\n");
    },
    fullDownloadLink() {
      return this.getUrlForExport(["Topic"]);
    },
    downloadLink() {
      return this.getUrlForExport([
        "Indicator",
        "Technology or Sector",
        "Country",
        "Year",
      ]);
    },
  },
  methods: {
    topicChanged() {
      this.setUpDefaultValues(this.topic);
    },
    setUpDefaultValues(topic, indicator) {
      if (topic === "Senior Management") {
        this.parameterValues = {
          ...this.parameterValues,
          Indicator:
            indicator ||
            "Share of female senior managers (country of headquarters)",
          Country: ["World", "IEA", "OECD"],
          "Technology or Sector": ["Energy", "Non-energy"],
          Year: 2023,
        };
      } else if (topic === "Employment") {
        this.parameterValues = {
          ...this.parameterValues,
          Indicator: indicator || "Gender wage gap conditional on skills",
          Country: [
            "Australia",
            "Canada",
            "France",
            "Germany",
            "Portugal",
            "United States",
          ],
          "Technology or Sector": ["Energy", "Non-energy"],
          Year: 2018,
        };
      } else if (topic === "Innovation") {
        this.parameterValues = {
          ...this.parameterValues,
          Indicator:
            indicator ||
            "Share of patents with at least one female inventor (fractional patent count by inventor country of residence)",
          Country: ["World", "IEA", "OECD"],
          "Technology or Sector": [
            "Total - all technologies",
            "Total - clean energy transition technologies",
            "Total - fossil fuel technologies",
          ],
          Year: 2021,
        };
      } else if (topic === "Entrepreneurship") {
        this.parameterValues = {
          ...this.parameterValues,
          Indicator:
            indicator ||
            "Share of start-ups with gender diverse founders (founding year)",
          Country: ["World", "IEA", "OECD"],
          "Technology or Sector": ["Energy", "Non-energy"],
          Year: 2022,
        };
      }
    },
    getUrlForExport(params) {
      const { apiRoute } = this;
      const parameterValues = { ...this.parameterValues, Topic: this.topic };
      const queryString = params
        .filter((key) => !!parameterValues[key])
        .map((key) => {
          if (Array.isArray(parameterValues[key])) {
            if (
              parameterValues[key].some(
                (p) => !!p && isNaN(p) && p.includes(",")
              )
            ) {
              return (
                key +
                "=" +
                encodeURIComponent(
                  parameterValues[key].map((p) => `"${p}"`).join(",")
                )
              );
            }
            return (
              key + "=" + encodeURIComponent(parameterValues[key].join(","))
            );
          } else if (
            isNaN(parameterValues[key]) &&
            parameterValues[key].includes(",")
          ) {
            return key + "=" + encodeURIComponent(`"${parameterValues[key]}"`);
          }
          return key + "=" + encodeURIComponent(parameterValues[key]);
        })
        .join("&")
        .concat("&csv=true");
      return `${apiRoute}?${queryString}`;
    },
    getUnitValue(unit) {
      switch (unit) {
        case "Percent":
          return "%";
        case "Number":
        case null:
        case undefined:
          return null;
        default:
          return unit;
      }
    },
    getParams(keys) {
      const params = {};
      for (let key of keys) {
        params[key] = this.parameterValues[key];
      }
      return params;
    },
  },
};
</script>
<style scoped>
.topic-description {
  white-space: pre-line;
}
.gender-portal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
}
.topic-logo {
  width: 85px;
  margin-right: 24px;
}
.m-block__links {
  overflow-y: auto;
  padding: 0 12px 12px 12px;
}
</style>
<style>
.indicator-box {
  z-index: 3 !important;
}
@media screen and (min-width: 730px) {
    .m-block__code .m-block--text {
        gap: 24px;
        flex-direction: row-reverse !important;
    }
}
</style>